/**
 * The product schema of an extraction recipe (T-365 step 6): the JSON Schema of ONE product.
 * Mirrors what nara-media accepts (app/shape/extraction.py), so most mistakes show before a save.
 * nara-media stays the judge; its own message can echo the schema, so a refusal shows a fixed sentence.
 */

const SUPPORTED_KEYWORDS = new Set([
  "type", "properties", "required", "enum", "items", "minItems", "maxItems", "minimum", "maximum", "format", "description", "title",
]);

export const STARTER_PRODUCT_SCHEMA = {
  type: "object",
  required: ["name"],
  properties: { name: { type: "string" }, price: { type: "number" } },
};

type Node = Record<string, unknown>;
const isNode = (value: unknown): value is Node => typeof value === "object" && value !== null && !Array.isArray(value);

/** True when every node uses supported keywords only. Keys under `properties` are field names, not keywords. */
function keywordsSupported(node: unknown): boolean {
  if (!isNode(node)) return false;
  if (!Object.keys(node).every((keyword) => SUPPORTED_KEYWORDS.has(keyword))) return false;
  const fields = isNode(node.properties) ? Object.values(node.properties) : [];
  return fields.every(keywordsSupported) && (!("items" in node) || keywordsSupported(node.items));
}

export type ParsedSchema = { schema: Node } | { error: string };

export function parseProductSchema(text: string): ParsedSchema {
  let schema: unknown;
  try {
    schema = JSON.parse(text);
  } catch {
    return { error: "productSchema.errors.invalidJson" };
  }
  if (!isNode(schema) || schema.type !== "object") return { error: "productSchema.errors.notObject" };
  if (!isNode(schema.properties) || Object.keys(schema.properties).length === 0) return { error: "productSchema.errors.noProperties" };
  if (!keywordsSupported(schema)) return { error: "productSchema.errors.keyword" };
  return { schema };
}

/** The admin op answers nara-media's refusal of an extraction recipe with this fixed message. */
export const extractionRefusal = (error: unknown): boolean => error instanceof Error && error.message.includes("extraction_recipe_error");
